// Home page component with optimized video loading and responsive CTA sections
import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import FeedbackSection from "../components/FeedbackSection";
import JyotirlingaSlider from "../components/JyotirlingaSlider";
import PanchaBhootaCTA from "../components/PanchaBhootaCTA";
import ArupadaiVeeduCTA from "../components/ArupadaiVeeduCTA";
import MuktiKshetrasKarnatakaCTA from "../components/MuktiKshetrasKarnatakaCTA";
import ShaktiCTA from "../components/ShaktiCTA";
import CharDhamCTA from "../components/CharDhamCTA";
import AmarnathCTA from "../components/AmarnathCTA";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import useSEO from "../hooks/useSEO";

function Home() {
  useSEO({
    title: "Akhandaa | Discover 51 Shakti Peethas & 12 Jyotirlingas Shrines",
    description: "Explore the divine beauty of India's sacred shrines. Discover detailed locations, maps, timings, histories, and emergency contact details for the 12 Jyotirlingas and 51 Shakti Peethas."
  });

  const [isPlaying, setIsPlaying] = useState(true);
  const [isMuted, setIsMuted] = useState(true);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const videoRef = useRef(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setVideoLoaded(true);
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) videoRef.current.pause();
      else videoRef.current.play();
      setIsPlaying(!isPlaying);
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  return (
    <>
      <Navbar />
      
      {/* Cinematic Video Background */}
      <div className="fixed inset-0 z-[-1] overflow-hidden bg-slate-950">
        <video
          ref={videoRef}
          autoPlay
          loop
          muted={isMuted}
          playsInline
          preload="auto"
          className="absolute inset-0 w-full h-full object-cover scale-[1.02] filter brightness-[0.8]"
        >
          {videoLoaded && <source src="/assets/videos/kumbha-vid-optimized.mp4" type="video/mp4" />}
        </video>
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/50 via-transparent to-slate-950/20"></div>
      </div>

      {/* Main Content Wrapper */}
      <div className="min-h-screen font-sans flex flex-col relative z-0 pt-[72px]">

        {/* HERO SECTION */}
        <div className="w-full flex flex-col items-center justify-center px-6 py-12 md:py-24 min-h-[calc(100vh-80px)] relative">
          <div className="text-center w-fit mx-auto px-4 animate-hero-fade opacity-0">
            <h1 className="mb-4 md:mb-6 flex flex-col items-center justify-center gap-4">
              <img 
                src="/Akhandaa.svg" 
                alt="Akhandaa Logo" 
                className="h-10 sm:h-14 md:h-20 lg:h-24 w-auto object-contain drop-shadow-2xl filter"
              />
              <span className="sr-only">Akhandaa | Discover 51 Shakti Peethas & 12 Jyotirlingas Shrines</span>
            </h1>
            <p className="text-sm sm:text-base md:text-xl text-white/90 mb-8 md:mb-12 drop-shadow-lg font-semibold tracking-wide uppercase">— Your path to divine discoveries —</p>

            {/* EXPLORE BUTTON */}
            <Link 
              to="/explore"
              onMouseEnter={() => import("../pages/Explore")}
              onTouchStart={() => import("../pages/Explore")}
              className="flex justify-center gap-3 items-center w-full shadow-2xl text-sm md:text-base bg-white backdrop-blur-md font-bold isolation-auto border-gray-50 before:absolute before:inset-y-0 before:my-auto before:-left-full before:w-full before:aspect-square before:rounded-full before:bg-sky-500 hover:text-white before:-z-10 before:transition-all before:duration-700 hover:before:left-0 hover:before:scale-150 relative z-10 px-8 py-3 md:px-10 md:py-4 overflow-hidden border-2 rounded-full group transition-transform active:scale-95 shadow-sky-200/50"
            >
              Explore
              <svg
                className="w-5 h-5 md:w-6 md:h-6 justify-end group-hover:rotate-90 group-hover:bg-white text-gray-50 ease-linear duration-300 rounded-full border border-gray-700 group-hover:border-none p-1 md:p-1.5 rotate-45 transition-all"
                viewBox="0 0 16 19"
              >
                <path
                  d="M7 18C7 18.5523 7.44772 19 8 19C8.55228 19 9 18.5523 9 18H7ZM8.70711 0.292893C8.31658 -0.0976311 7.68342 -0.0976311 7.29289 0.292893L0.928932 6.65685C0.538408 7.04738 0.538408 7.68054 0.928932 8.07107C1.31946 8.46159 1.95262 8.46159 2.34315 8.07107L8 2.41421L13.6569 8.07107C14.0474 8.46159 14.6805 8.46159 15.0711 8.07107C15.4616 7.68054 15.4616 7.04738 15.0711 6.65685L8.70711 0.292893ZM9 18L9 1H7L7 18H9Z"
                  className="fill-gray-800"
                ></path>
              </svg>
            </Link>
          </div>

          {/* VIDEO CONTROLS - Adjusted for mobile */}
          <div className="absolute right-6 bottom-8 md:right-8 md:bottom-12 flex gap-3 md:gap-4">
            {/* Play/Pause */}
            <button 
              onClick={togglePlay}
              className="w-10 h-10 md:w-12 md:h-12 bg-white/30 hover:bg-white/80 backdrop-blur-xl border border-white/20 text-gray-900 rounded-full flex items-center justify-center transition-all shadow-xl active:scale-90"
            >
              {isPlaying ? (
                <svg className="w-4 h-4 md:w-5 md:h-5 fill-current" viewBox="0 0 24 24"><path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/></svg>
              ) : (
                <svg className="w-4 h-4 md:w-5 md:h-5 fill-current ml-1" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>
              )}
            </button>
            {/* Mute/Unmute */}
            <button 
              onClick={toggleMute}
              className="w-10 h-10 md:w-12 md:h-12 bg-white/30 hover:bg-white/80 backdrop-blur-xl border border-white/20 text-gray-900 rounded-full flex items-center justify-center transition-all shadow-xl active:scale-90"
            >
              {isMuted ? (
                <svg className="w-4 h-4 md:w-5 md:h-5 fill-current" viewBox="0 0 24 24"><path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77zM3 9v6h4l5 5V4L7 9H3z"/></svg>
              ) : (
                <svg className="w-4 h-4 md:w-5 md:h-5 fill-current" viewBox="0 0 24 24"><path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/></svg>
              )}
            </button>
          </div>
        </div>

        <JyotirlingaSlider />

        <PanchaBhootaCTA />

        <ArupadaiVeeduCTA />

        <MuktiKshetrasKarnatakaCTA />

        <ShaktiCTA />

        <CharDhamCTA />

        <AmarnathCTA />

        <FeedbackSection />

        <Footer />
      </div>
    </>
  );
}

export default Home;
